import { useEffect } from 'react';
import { DRAG_SPEED_MULTIPLIER, DRAG_THRESHOLD_PX } from './scrollControl.constants';
import { ScrollGestureState } from './scrollGestureState';

// Mouse and pen only — touch is deliberately left to the browser.
export const useDragToScroll = (
  scrollContainerRef: React.RefObject<HTMLDivElement>,
  { drag, programmatic }: ScrollGestureState,
) => {
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return undefined;
    }

    const dragState = drag.current;
    const programmaticScroll = programmatic.current;

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') {
        return;
      }
      if (event.button !== 0) {
        return;
      }

      dragState.isPointerDown = true;
      dragState.suppressClick = false;
      dragState.startX = event.clientX;
      dragState.scrollLeft = scrollContainer.scrollLeft;
      dragState.pointerId = event.pointerId;
      dragState.isDragging = false;
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (!dragState.isPointerDown) {
        return;
      }

      const deltaX = event.clientX - dragState.startX;
      if (!dragState.isDragging && Math.abs(deltaX) > DRAG_THRESHOLD_PX) {
        dragState.isDragging = true;
        dragState.suppressClick = true;
        programmaticScroll.isActive = false;
        scrollContainer.classList.add('is-dragging');
        scrollContainer.setPointerCapture?.(event.pointerId);
      }
      if (dragState.isDragging) {
        scrollContainer.scrollLeft = dragState.scrollLeft - deltaX * DRAG_SPEED_MULTIPLIER;
      }
    };

    const endDrag = () => {
      if (!dragState.isPointerDown) {
        return;
      }

      if (dragState.isDragging) {
        dragState.isDragging = false;
        scrollContainer.classList.remove('is-dragging');
        if (dragState.pointerId !== null && scrollContainer.hasPointerCapture?.(dragState.pointerId)) {
          scrollContainer.releasePointerCapture?.(dragState.pointerId);
        }
      }

      dragState.isPointerDown = false;
      dragState.pointerId = null;

      // Cleared next tick, once the click this drag produced has been swallowed.
      if (dragState.suppressClick) {
        window.setTimeout(() => {
          dragState.suppressClick = false;
        }, 0);
      }
    };

    const handleClickCapture = (event: MouseEvent) => {
      if (!dragState.suppressClick) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      dragState.suppressClick = false;
    };

    const handleDragStart = (event: DragEvent) => {
      event.preventDefault();
    };

    scrollContainer.addEventListener('pointerdown', handlePointerDown);
    scrollContainer.addEventListener('pointermove', handlePointerMove);
    scrollContainer.addEventListener('pointerup', endDrag);
    scrollContainer.addEventListener('pointercancel', endDrag);
    scrollContainer.addEventListener('pointerleave', endDrag);
    scrollContainer.addEventListener('click', handleClickCapture, true);
    scrollContainer.addEventListener('dragstart', handleDragStart, true);

    return () => {
      scrollContainer.removeEventListener('pointerdown', handlePointerDown);
      scrollContainer.removeEventListener('pointermove', handlePointerMove);
      scrollContainer.removeEventListener('pointerup', endDrag);
      scrollContainer.removeEventListener('pointercancel', endDrag);
      scrollContainer.removeEventListener('pointerleave', endDrag);
      scrollContainer.removeEventListener('click', handleClickCapture, true);
      scrollContainer.removeEventListener('dragstart', handleDragStart, true);
    };
  }, [scrollContainerRef, drag, programmatic]);
};
